/**
 * Helpers for honoring HTTP 503 + Retry-After from the backend.
 *
 * When gorilla's circuit breakers shed load they return a real HTTP 503 on
 * /graphql with a Retry-After header (integer seconds) hinting when the
 * breaker will admit probes again. Retrying sooner is pointless and keeps the
 * breaker open, so the retry link waits out the hint instead of using its
 * usual fast exponential backoff.
 */

/** Cap on a server-provided Retry-After so a bad header can't stall panels. */
export const MAX_RETRY_AFTER_MS = 60_000;

/**
 * Total tries (initial + retries) for a 503. Deliberately lower than the
 * 6 used for other 5xx: a 503 means the server is shedding load on purpose.
 */
export const MAX_ATTEMPTS_503 = 3;

/**
 * The error here is the ServerError thrown by apollo-link-http on non-2xx
 * responses: {statusCode, response} where response is the fetch Response.
 */
export function is503Error(error: any): boolean {
  return error != null && error.statusCode === 503;
}

/**
 * Reads the Retry-After header off a ServerError, in ms. Returns null when
 * the header is missing/unreadable (older backend, proxy 503, CORS) or not a
 * non-negative integer (the HTTP-date form is not emitted by our backend).
 */
export function getRetryAfterMs(error: any): number | null {
  const headers = error?.response?.headers;
  if (headers == null || typeof headers.get !== 'function') {
    return null;
  }
  const value = headers.get('retry-after');
  if (value == null || !/^\d+$/.test(value.trim())) {
    return null;
  }
  const seconds = parseInt(value, 10);
  return Math.min(seconds * 1000, MAX_RETRY_AFTER_MS);
}

/**
 * Delay before re-attempting a 503'd operation: the server-directed
 * Retry-After plus up to 1s of jitter (so panels don't stampede the
 * half-open breaker at the same instant), or a jittered 5-15s when the
 * response carried no usable header.
 */
export function get503RetryDelayMs(
  error: any,
  random: () => number = Math.random
): number {
  const retryAfterMs = getRetryAfterMs(error);
  if (retryAfterMs != null) {
    return retryAfterMs + random() * 1000;
  }
  return 5000 + random() * 10_000;
}
